import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useRef,
} from 'react';
import { PhoneVerificationForm } from 'pages/PhoneVerification/PhoneVerification';
import classes from 'classnames';
import styles from './DigitalCodeInput.module.scss';

export type DigitalCodeInputProps = {
  inputClassName?: string;
  values: PhoneVerificationForm;
  onChange?: (key: string, value: string) => void;
  dataTest?: string;
  name: string;
};

const ONLY_NUMBER = /^\d+$/;

export default function DigitalCodeInput({
  inputClassName,
  values,
  onChange,
}: DigitalCodeInputProps) {
  const fields = useRef<Array<HTMLInputElement | null>>([]);

  const handleMultipleChars = (event: ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    const prevValue = values[event.target.name];
    const rawValue = event.target.value.replace(prevValue, '');

    if (rawValue.length !== 4) return;

    const index = Number(event.target.id);

    // Handle multi-character input (e.g. SMS autofill bypassing keypress)
    if (rawValue.length > 1) {
      const chars = rawValue
        .split('')
        .splice(0, Object.keys(values).length - index);
      const hasNonValidChar = chars.some((char) => !ONLY_NUMBER.test(char));
      if (hasNonValidChar) {
        event.target.value = prevValue || '';
        return;
      }
      chars.forEach((char, i) => {
        const fieldKey = Object.keys(values)[index + i];
        if (fieldKey) onChange?.(fieldKey, char);
      });
      const lastIndex = Math.min(
        index + chars.length,
        Object.keys(values).length - 1,
      );
      fields.current[lastIndex]?.focus();
      return;
    }
  };

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      // @ts-ignore
      const prevValue = values[event.target.name];
      const index = Number(event.target.id);
      const rawValue = event.target.value.replace(prevValue, '');

      // Handle multi-character input
      if (rawValue.length > 1) {
        handleMultipleChars(event);
        return;
      }

      // Handle one character
      const nextValue = rawValue;
      event.target.value = nextValue !== undefined ? nextValue : '';

      onChange?.(event.target.name, nextValue);

      if (Boolean(prevValue) && !nextValue) return; // Client removed the number. Don't change the focus

      // Navigate to next input
      if (event.target.value) {
        fields.current[index + 1]?.focus();
      } else {
        fields.current[index - 1]?.focus();
      }
    },
    [values, onChange, fields],
  );

  // Function for keyboard behaviour (no mouse)
  const handleKeyDown = useCallback(
    (
      event: KeyboardEvent<HTMLInputElement>,
      key: keyof PhoneVerificationForm,
    ) => {
      const index = Number(event.currentTarget.id);
      const isBackspace = event.key === 'Backspace';
      const isRightArrow = event.key === 'ArrowRight';
      const isLeftArrow = event.key === 'ArrowLeft';

      const value = values[key];

      // Navigate input fields with keys
      if (isLeftArrow) {
        fields.current[index - 1]?.focus();
      } else if (isRightArrow) {
        fields.current[index + 1]?.focus();
      } else if (isBackspace) {
        // Remove value or navigate to previous one if it's possible
        if (value) {
          onChange?.(key, value);
        } else {
          fields.current[index - 1]?.focus();
          event.preventDefault();
        }
      }
    },
    [values],
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const ONLY_NUMBER = /^\d+$/;

      if (!ONLY_NUMBER.test(event.key)) {
        // Allow only digit inputs
        event.preventDefault();
      }
    },
    [],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const ONLY_NUMBER = /^\d+$/;
      const paste: string[] = event.clipboardData
        .getData('text')
        .split('')
        .splice(0, Object.keys(values).length);

      const hasNonValidChar = paste.some((char) => !ONLY_NUMBER.test(char));

      if (hasNonValidChar) {
        return;
      }

      if (paste.length >= Object.keys(values).length) {
        // All inputs are full, nowhere to navigate anymore
        fields.current[paste.length - 1]?.focus();
      } else {
        // Go to next input if available
        fields.current[paste.length]?.focus();
      }
    },
    [values],
  );

  return (
    <>
      {values &&
        Object.keys(values).map((key, index) => {
          return (
            <input
              key={index}
              id={index.toString()}
              inputMode="numeric"
              type="text"
              pattern="[0-9]*"
              autoComplete={index === 0 ? 'one-time-code' : undefined}
              className={classes([
                styles.border,
                styles['digital-code-input'],
                inputClassName,
              ])}
              data-test={`input-${index}`}
              name={key}
              // @ts-ignore
              value={values[key] || ''}
              onChange={handleChange}
              onKeyDown={(e) =>
                handleKeyDown(e, key as keyof PhoneVerificationForm)
              }
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              ref={(node) => {
                if (node) fields.current[index] = node;
              }}
            />
          );
        })}
    </>
  );
}
